import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { useDuplexerSubscription } from './use-duplexer-subscription';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { fetchConnectors, type ConnectorStatus } from './connectors';
import { useWorkspaceGoals, type WebGoal } from './goals';
import { useMyAgents, type AgentSummary } from './my-agents';
import { useRoutines, type WebRoutine } from './routines';
import { useWorkspaces, type WorkspaceSummary } from './workspaces';
import { useTasks, type WebTask } from './tasks';
import { useAgentWorkspaceFiles, type AgentWorkspaceFileItem } from './agent-workspace-files';
import { useKnowledge, type KnowledgeState } from './knowledge';
import { keyedThrottle, onTurnProgress } from './turn-progress';
import type { KnowledgeEntry } from './knowledge-groups';
import {
  chatStatusHasContent,
  readConversationCreations,
  resolveChatStatus,
  type ChatStatus,
  type ConversationCreations,
} from './conversation-creations';

export type ChatStatusData = ChatStatus<
  WorkspaceSummary,
  AgentSummary,
  WebRoutine,
  WebGoal,
  ConnectorStatus,
  WebTask,
  AgentWorkspaceFileItem,
  KnowledgeEntry
>;

export type ChatStatusState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  /** `settled`: every live list has answered, so what the panel shows is complete, not a partial first frame. */
  | { status: 'ready'; data: ChatStatusData; hasContent: boolean; settled: boolean };

async function fetchConversationCreations(conversationId: string): Promise<ConversationCreations> {
  const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(conversationId)}/creations`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return readConversationCreations(((await res.json()) as { creations?: unknown }).creations);
}

/** Re-asks what the conversation created. Called when one of its turns completes, and during the turn after
 *  each tool step (`lib/turn-progress.ts`); the live lists below already follow their own duplexers. */
export function refreshConversationCreations(conversationId: string | null | undefined): void {
  if (!conversationId) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversationCreations(conversationId) });
}

const TURN_PROGRESS_REFRESH_MS = 1500;
const refreshOnTurnProgress = keyedThrottle(refreshConversationCreations, TURN_PROGRESS_REFRESH_MS, {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
});

function useTurnProgressRefresh(conversationId: string | null) {
  useEffect(() => {
    if (!conversationId) return;
    return onTurnProgress((id) => {
      if (id === conversationId) refreshOnTurnProgress(id);
    });
  }, [conversationId]);
}

const KNOWLEDGE_CATCH_UP_MS = 10_000;

/**
 * The knowledge store loads once, and a save lands through a consumer AFTER the turn that made it, so an entry
 * this conversation names can be missing from a store loaded a moment too early. While one is, the store is
 * re-read on the file-system signal, at most every 10s, until it shows up.
 */
function useKnowledgeCatchUp(paths: string[] | undefined, knowledge: KnowledgeState, refresh: () => Promise<void>) {
  const known = knowledge.kind === 'ready' ? new Set(knowledge.groups.flatMap((group) => group.entries.map((entry) => entry.path))) : null;
  const missing = Boolean(known && paths?.some((path) => !known.has(path)));
  const lastRef = useRef(0);
  const catchUp = useRef(() => {});
  catchUp.current = () => {
    if (!missing || Date.now() - lastRef.current < KNOWLEDGE_CATCH_UP_MS) return;
    lastRef.current = Date.now();
    void refresh();
  };
  const pathsKey = paths?.join('|') ?? '';
  useEffect(() => {
    catchUp.current();
  }, [missing, pathsKey]);
  useDuplexerSubscription(IndividualsDuplexerChannel.FILE_SYSTEM_CHANGED, () => catchUp.current(), ['created', 'updated']);
}

/**
 * The chat status panel's model: what this conversation created, resolved against the live lists the app
 * already holds (the same cache entries the Spaces, Agents, Routines, Goals, Connectors, Tasks, Files and
 * Knowledge screens read), so a switch flipped or a connector connected anywhere shows here at once.
 *
 * Mount it only where the panel can show — every read below starts on mount, except the files and the
 * knowledge, which wait until the conversation has written some. A live list that failed counts as empty for
 * its own section and leaves the others standing.
 */
export function useChatStatus(conversationId: string | null): ChatStatusState {
  const creations = useQuery({
    queryKey: queryKeys.conversationCreations(conversationId ?? undefined),
    queryFn: () => fetchConversationCreations(conversationId as string),
    enabled: !!conversationId,
  });
  useTurnProgressRefresh(conversationId);
  const workspaces = useWorkspaces();
  const agents = useMyAgents();
  const { state: routines } = useRoutines();
  const goals = useWorkspaceGoals(undefined);
  const connectors = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const { state: tasks } = useTasks(undefined);
  const taskList = tasks.kind === 'ready' ? tasks.tasks : null;
  const wroteFiles = (creations.data?.files.length ?? 0) > 0;
  const savedKnowledge = (creations.data?.knowledge.length ?? 0) > 0;
  const files = useAgentWorkspaceFiles({ enabled: wroteFiles });
  const { state: knowledge, refresh: refreshKnowledge } = useKnowledge({ enabled: savedKnowledge });
  useKnowledgeCatchUp(creations.data?.knowledge, knowledge, refreshKnowledge);

  const live = useMemo(
    () => ({
      workspaces: workspaces.status === 'ready' ? workspaces.workspaces : [],
      agents: agents.status === 'ready' ? agents.team.agents : [],
      routines: routines.kind === 'ready' ? routines.routines : [],
      goals: goals.status === 'ready' ? goals.goals : [],
      connectors: connectors.data?.connectors ?? [],
      tasks: taskList ?? [],
      files: files.status === 'ready' ? files.items : [],
      knowledge: knowledge.kind === 'ready' ? knowledge.groups.flatMap((group) => group.entries) : [],
    }),
    [workspaces, agents, routines, goals, connectors.data, taskList, files, knowledge],
  );
  const liveSettled =
    workspaces.status !== 'loading' &&
    agents.status !== 'loading' &&
    routines.kind !== 'loading' &&
    goals.status !== 'loading' &&
    !connectors.isLoading &&
    tasks.kind !== 'loading' &&
    (!wroteFiles || files.status !== 'loading') &&
    (!savedKnowledge || knowledge.kind !== 'loading');

  return useMemo<ChatStatusState>(() => {
    if (!conversationId) return { status: 'idle' };
    if (creations.data) {
      const data = resolveChatStatus(conversationId, creations.data, live);
      return { status: 'ready', data, hasContent: chatStatusHasContent(data), settled: liveSettled };
    }
    return creations.status === 'error' ? { status: 'error' } : { status: 'loading' };
  }, [conversationId, creations.data, creations.status, live, liveSettled]);
}
